import QRCodeStyling from 'qr-code-styling';
import { buildQrStylingOptions } from './qrDesign';

const FONT_MAP = {
  'sans-serif': 'bold 14px ui-sans-serif, system-ui, sans-serif',
  serif: 'bold 14px ui-serif, Georgia, serif',
  monospace: 'bold 13px ui-monospace, monospace',
};

async function renderQrBlob(qrValue, style) {
  const qr = new QRCodeStyling(buildQrStylingOptions(qrValue, style, style.size));
  const blob = await qr.getRawData('png');
  if (!blob) throw new Error('Could not render QR code');
  return blob;
}

function loadImageFromBlob(blob) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = reject;
    img.src = url;
  });
}

function drawFramedCanvas(img, style) {
  const bannerH = style.frameType === 'banner' ? 40 : 0;
  const border = style.frameType === 'border' ? 12 : style.frameType === 'rounded' ? 16 : 0;
  const pad = style.frameType === 'rounded' ? 12 : 8;

  const innerW = img.width + pad * 2;
  const innerH = img.height + pad * 2;
  const canvas = document.createElement('canvas');
  canvas.width = innerW + border * 2;
  canvas.height = innerH + border * 2 + bannerH;

  const ctx = canvas.getContext('2d');
  ctx.fillStyle = style.bgColor;
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  const ox = border;
  const oy = border;

  if (style.frameType === 'rounded') {
    const r = 24;
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.roundRect(ox, oy, innerW, innerH + bannerH, r);
    ctx.fill();
    ctx.strokeStyle = style.frameColor;
    ctx.lineWidth = 5;
    ctx.stroke();
  } else if (style.frameType === 'border') {
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(ox, oy, innerW, innerH + bannerH);
    ctx.strokeStyle = style.frameColor;
    ctx.lineWidth = 6;
    ctx.strokeRect(ox, oy, innerW, innerH + bannerH);
  } else if (style.frameType === 'banner') {
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(ox, oy, innerW, innerH);
    ctx.strokeStyle = style.frameColor;
    ctx.lineWidth = 4;
    ctx.strokeRect(ox, oy, innerW, innerH + bannerH);
  }

  ctx.drawImage(img, ox + pad, oy + pad);

  if (style.frameType === 'banner') {
    const by = oy + innerH;
    ctx.fillStyle = style.frameColor;
    ctx.fillRect(ox, by, innerW, bannerH);
    ctx.fillStyle = style.frameTextColor;
    ctx.font = FONT_MAP[style.frameFont] || FONT_MAP['sans-serif'];
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(
      (style.framePhrase || 'SCAN ME').toUpperCase(),
      ox + innerW / 2,
      by + bannerH / 2
    );
  }

  return canvas;
}

function triggerDownload(url, filename) {
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

export async function downloadStyledPng(qrValue, style) {
  const blob = await renderQrBlob(qrValue, style);

  if (style.frameType === 'none') {
    triggerDownload(URL.createObjectURL(blob), 'pixelutility-qrcode.png');
    return;
  }

  const img = await loadImageFromBlob(blob);
  const canvas = drawFramedCanvas(img, style);
  canvas.toBlob((out) => {
    if (out) triggerDownload(URL.createObjectURL(out), 'pixelutility-qrcode.png');
  }, 'image/png');
}

export async function downloadStyledSvg(qrValue, style) {
  if (style.frameType !== 'none') {
    await downloadStyledPng(qrValue, style);
    return;
  }

  const qr = new QRCodeStyling({
    ...buildQrStylingOptions(qrValue, style, style.size),
    type: 'svg',
  });
  await qr.download({ name: 'pixelutility-qrcode', extension: 'svg' });
}
